import { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsPerson, BsSearch } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';
import Menu from '#globalComponents/Overlay/Menu';
import Popover from '#globalComponents/Overlay/Popover';
import { useDebouncedValue } from '#globalHooks/useDebouncedValue';

import { useContactListSearch } from '../../models/queries';

// The search returns one hit per list membership; the preview only needs each recipient once.
const uniqueHits = (hits) => [...new Map(hits.map((hit) => [`${hit.type}:${hit.entityId}`, hit])).values()];

/**
 * Search field for picking a real contact-list recipient whose values fill a template's placeholders.
 */
const RecipientSearchPicker = ({ recipient, onChange, listIds }) => {
	const [query, setQuery] = useState('');
	const [open, setOpen] = useState(false);
	const anchorRef = useRef(null);
	const debounced = useDebouncedValue(query);
	const { data, isFetching } = useContactListSearch(debounced, { listIds });
	const hits = uniqueHits([...(data?.contacts ?? []), ...(data?.companies ?? [])]);

	const pick = (hit) => {
		onChange({
			name: hit.name,
			...(hit.type === 'contact' ? { crmContactId: hit.entityId } : { crmCompanyId: hit.entityId }),
		});
		setQuery('');
		setOpen(false);
	};

	return (
		<div ref={anchorRef} className="relative w-full">
			<BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bgBase-800" />
			<TextInput
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				// The picker sits inside forms (template editor, test send); Enter must not submit them.
				onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
				placeholder={
					recipient
						? `Vorschau für ${recipient.name} – anderen Empfänger suchen …`
						: listIds?.length
							? 'Empfänger dieser Kampagne suchen …'
							: 'Empfänger aus einer Kontaktliste suchen …'
				}
				className="pl-9"
			/>
			<Popover
				open={open && query.trim().length >= 2}
				onClose={() => setOpen(false)}
				anchorRef={anchorRef}
				align="stretch"
			>
				<Menu>
					{hits.length === 0 && <Menu.Empty>{isFetching ? 'Suche …' : 'Keine Treffer'}</Menu.Empty>}
					{hits.slice(0, 10).map((hit) => (
						<Menu.Item
							key={`${hit.type}:${hit.entityId}`}
							icon={hit.type === 'contact' ? BsPerson : BsBuilding}
							trailing={hit.listName}
							onClick={() => pick(hit)}
						>
							{hit.name || hit.matchedValue}
						</Menu.Item>
					))}
				</Menu>
			</Popover>
		</div>
	);
};

RecipientSearchPicker.propTypes = {
	// { name, crmContactId } or { name, crmCompanyId }
	recipient: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	// Only offer recipients of these lists (e.g. a campaign's); omitted = all lists.
	listIds: PropTypes.arrayOf(PropTypes.string),
};

export default RecipientSearchPicker;
